import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Send, ShieldCheck, Hourglass, Flag, Ban, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { KidAvatar } from "../components/Brand";
import { ReportDialog } from "../components/ReportDialog";
import { kidApi, streamUrl, errMsg, KID_KEY } from "../lib/api";
import { catById, timeAgo } from "../lib/constants";
import { useAuth } from "../context/AuthContext";

export default function Watch() {
  const { id } = useParams();
  const { kid } = useAuth();
  const [video, setVideo] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const nav = useNavigate();

  const block = async () => {
    if (!window.confirm(`Block ${video.author?.name}? You won't see their videos or comments anymore.`)) return;
    try {
      await kidApi.post(`/kid/blocks/${video.kid_id}`);
      toast.success(`${video.author?.name} is blocked`);
      nav("/kid");
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => {
    kidApi.get(`/videos/${id}`).then((r) => setVideo(r.data)).catch(() => setVideo(false));
    kidApi.get(`/videos/${id}/comments`).then((r) => setComments(r.data)).catch(() => {});
  }, [id]);

  const cheer = async () => {
    try {
      const { data } = await kidApi.post(`/kid/videos/${id}/cheer`);
      setVideo((v) => ({ ...v, ...data }));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleSave = async () => {
    try {
      const { data } = video.favorited ? await kidApi.delete(`/kid/favorites/${id}`) : await kidApi.post(`/kid/favorites/${id}`);
      setVideo((v) => ({ ...v, ...data }));
      toast.success(data.favorited ? "Saved to your Favorites!" : "Removed from Favorites");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await kidApi.post(`/kid/videos/${id}/comments`, { text });
      setComments((c) => [data, ...c]);
      setText("");
      toast.success("Sent! A grown-up will check it soon.");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  if (video === false) return <p data-testid="video-not-found" className="font-display text-2xl">Video not found.</p>;
  if (!video) return <p className="font-display text-xl">Loading…</p>;
  const cat = catById(video.category);
  const approved = video.status === "approved";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
      <div className="lg:col-span-8">
        <Link to="/kid" data-testid="watch-back-btn" className="zn-chip bg-white mb-4 sm:mb-5"><ArrowLeft size={16} /> Back</Link>
        <div className="zn-card overflow-hidden bg-[#1B1A2E] -mx-1 sm:mx-0 grid place-items-center">
          <video data-testid="video-player" src={streamUrl(id, localStorage.getItem(KID_KEY))} controls playsInline autoPlay muted loop preload="auto" controlsList="nodownload noplaybackrate" disablePictureInPicture className="w-full max-h-[68svh] lg:max-h-[70vh] min-h-[200px] object-contain bg-black" />
        </div>
        <div className="mt-5 flex items-start gap-3 sm:gap-4">
          <Link to={`/kid/creator/${video.kid_id}`} data-testid="video-creator-link" className="shrink-0"><KidAvatar avatar={video.author?.avatar} color={video.author?.color} frame={video.author?.frame} size={48} /></Link>
          <div className="flex-1 min-w-0">
            <h1 data-testid="video-title" className="text-2xl sm:text-4xl font-bold leading-tight break-words">{video.title}</h1>
            <p className="text-sm sm:text-base text-[#1B1A2E]/60"><Link to={`/kid/creator/${video.kid_id}`} className="hover:underline">{video.author?.name}</Link> · <span style={{ color: "#1B1A2E" }}>{cat.label}</span> · {timeAgo(video.created_at)}</p>
          </div>
        </div>
        {approved && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button data-testid="cheer-btn" onClick={cheer} className={`zn-btn !py-2.5 !px-4 ${video.cheered ? "bg-[#FF8FC7]" : "bg-white"}`}>
              <Heart size={18} fill={video.cheered ? "#1B1A2E" : "none"} /> <span data-testid="cheer-count">{video.cheer_count}</span> Cheers
            </button>
            <button data-testid="save-favorite-btn" onClick={toggleSave} className={`zn-chip !py-2.5 ${video.favorited ? "bg-[#FFD23F]" : "bg-white"}`}><Bookmark size={15} fill={video.favorited ? "#1B1A2E" : "none"} /> {video.favorited ? "Saved" : "Save"}</button>
            {video.kid_id !== kid.id && (
              <>
                <button data-testid="report-video-btn" onClick={() => setReport({ type: "video", id })} className="zn-chip bg-white !py-2.5"><Flag size={15} /> Report</button>
                <button data-testid="block-creator-btn" onClick={block} className="zn-chip bg-white !py-2.5"><Ban size={15} /> Block</button>
              </>
            )}
          </div>
        )}
        {video.description && <p className="mt-4 zn-card p-4 sm:p-5 bg-[#FFF]">{video.description}</p>}
      </div>

      <aside className="lg:col-span-4">
        <div className="zn-card p-5 sm:p-6 bg-white">
          <h2 className="text-2xl font-bold mb-1">Comments</h2>
          <p className="text-xs flex items-center gap-1 text-[#1B1A2E]/60 mb-4"><ShieldCheck size={14} /> AI safety check + grown-up approval</p>
          {approved && kid.settings?.comments_enabled ? (
            <form onSubmit={send} className="flex gap-2 mb-6">
              <input data-testid="comment-input" className="zn-input !py-2.5" placeholder="Say something kind…" value={text} onChange={(e) => setText(e.target.value)} maxLength={300} enterKeyHint="send" autoComplete="off" />
              <button data-testid="comment-submit-btn" disabled={busy || !text.trim()} className="zn-btn bg-[#7B5CFA] text-white !px-4 !py-2 shrink-0" aria-label="Send comment"><Send size={18} /></button>
            </form>
          ) : (
            <p data-testid="comments-disabled" className="text-sm mb-6 text-[#1B1A2E]/60">{approved ? "Comments are turned off by your grown-up." : "Comments open once this video is approved."}</p>
          )}
          <ul className="space-y-4" data-testid="comment-list">
            {comments.length === 0 && <li className="text-sm text-[#1B1A2E]/60">No comments yet.</li>}
            {comments.map((c) => (
              <li key={c.id} data-testid={`comment-${c.id}`} className="flex gap-3">
                <KidAvatar avatar={c.author?.avatar} color={c.author?.color} frame={c.author?.frame} size={34} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{c.author?.name} <span className="font-normal text-[#1B1A2E]/50">{timeAgo(c.created_at)}</span></p>
                  <p className="break-words">{c.text}</p>
                  {c.status === "pending" && <span className="text-xs inline-flex items-center gap-1 text-[#B45309]"><Hourglass size={12} /> Waiting for approval</span>}
                </div>
                {c.kid_id !== kid.id && c.status === "approved" && (
                  <button data-testid={`report-comment-${c.id}`} onClick={() => setReport({ type: "comment", id: c.id })} className="w-10 h-10 shrink-0 grid place-items-center rounded-full hover:bg-[#FFF7EC]" title="Report comment" aria-label="Report comment"><Flag size={15} /></button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </aside>
      <ReportDialog target={report} onClose={() => setReport(null)} />
    </div>
  );
}
